import { HttpClient } from '@angular/common/http';
import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import * as OrgPost from '../../../server/routes/orgs/post';
import {
  generatePath,
  orgNameMaxLength,
} from '../../../server/services/utils.service';
import { AuthUserService } from '../auth-user.service';
import { TitleService } from '../title.service';

interface OrgCreateForm {
  name: FormControl<string>;
}

@Component({
  selector: 'app-org-create',
  templateUrl: './org-create.component.html',
  styleUrls: ['./org-create.component.scss'],
  standalone: false,
})
export class OrgCreateComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private titleService = inject(TitleService);
  private router = inject(Router);
  private http = inject(HttpClient);

  form: FormGroup<OrgCreateForm>;
  accountId: string;
  email: string;

  submitSuccess = false;
  waiting = false;
  formErrorMessage?: string;
  disableButton = false;

  subscriptions = new Subscription();

  ngOnInit(): void {
    this.titleService.set('Create organization');
    this.form = new FormGroup<OrgCreateForm>({
      name: new FormControl('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.maxLength(orgNameMaxLength),
        ],
      }),
    });

    this.authUserService.account.subscribe((account) => {
      if (!account) throw new Error('account no set'); // This should never happen
      this.accountId = account.accountId;
      this.email = account.email;
    });
  }

  submit(): void {
    if (this.form.invalid) return;

    // Clear state from previous submissions
    this.formErrorMessage = undefined;

    const name = this.form.controls.name.value;

    this.waiting = true;

    const path = generatePath<OrgPost.ReqParams>('/api/orgs', {});
    const body: OrgPost.ReqBody = { name };
    const httpSubscribe = this.http
      .post<OrgPost.ResBody>(path, body)
      .subscribe({
        next: ({ org, accountId }) => {
          this.waiting = false;
          this.authUserService.processCreateOrg({ accountId, org });
          this.router.navigate(['/']); // #PostOrgCreationPage
        },
        error: (error) => {
          this.waiting = false;
          this.formErrorMessage = 'There was a problem submitting the form.';
          console.error(error.message);
        },
      });
    this.subscriptions.add(httpSubscribe);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
